import React, {useEffect, useMemo} from 'react';
import {capitalize} from 'src/utils/helpers';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Checkbox, TableHead} from '@mui/material';

interface Data {
    variable: string;
    value: string[];
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
}

const headCells: readonly HeadCell[] = [
    {
        id: 'variable',
        numeric: false,
        disablePadding: true,
        label: 'VARIABLE'
    },
    {
        id: 'value',
        numeric: true,
        disablePadding: false,
        label: 'VALUE'
    }
];

interface SelectVariabledDBAProps {
    formData: {group_options: {name: string; value?: string}[]};
    setFormData: any;
    handleNext: any;
    groupLevels: {[s: string]: string[]};
    error: any;
    setError: any;
}

const SelectVariabledDBA = ({formData, setFormData, handleNext, groupLevels, error, setError}: SelectVariabledDBAProps) => {
    const {group_options: selectedGroupOption} = formData;

    const rows: Data[] = useMemo(() => {
        return Object.entries(groupLevels)
            .map(([key, value]) => {
                return {
                    variable: key.split('_').map(capitalize).join(' '),
                    value: value
                };
            })
            .sort((a, b) => a.variable.localeCompare(b.variable));
    }, [groupLevels]);

    useEffect(() => {
        selectedGroupOption.length && selectedGroupOption.length > 2 ? setError(`Please select between 1 and 2 options.`) : setError(null);
    }, [selectedGroupOption]);

    useEffect(() => {
        error || selectedGroupOption.length < 1 ? handleNext(false) : handleNext(true);
    }, [error, selectedGroupOption]);

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = rows.map((n: Data) => ({name: n.variable}));
            setFormData((prev: any) => {
                return {
                    ...prev,
                    group_options: newSelected
                };
            });
            return;
        }
        setFormData((prev: any) => {
            return {
                ...prev,
                group_options: []
            };
        });
    };

    /** Handle clicking the select checkbox for a group option */
    const handleClick = (name: string) => {
        const selectedIndex = selectedGroupOption?.findIndex((item: any) => item.name === name);
        let newSelected: {name: string}[] = [];

        if (selectedIndex === -1) {
            newSelected = [...selectedGroupOption, {name}];
        } else {
            newSelected = [...selectedGroupOption.slice(0, selectedIndex), ...selectedGroupOption.slice(selectedIndex + 1)];
        }
        setFormData((prev: any) => {
            return {
                ...prev,
                group_options: newSelected
            };
        });
    };

    /** Check whether a group option is selected */
    const isSelected = (name: string) => selectedGroupOption?.map((e: any) => e.name).indexOf(name) !== -1;

    return (
        <Box p={4} sx={{width: '100%', borderRadius: '12px', bgcolor: 'surface-container-lowest'}}>
            <TableContainer sx={{border: '1px solid', borderColor: 'outline', borderRadius: '6px'}}>
                <Table aria-labelledby="tableTitle" size="medium">
                    <TableHead>
                        <TableRow sx={{bgcolor: 'surface-container-highest'}}>
                            <TableCell width={'15%'} align="left">
                                <Checkbox
                                    indeterminate={selectedGroupOption?.length > 0 && selectedGroupOption?.length < rows?.length}
                                    checked={rows?.length > 0 && selectedGroupOption?.length === rows?.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                            {headCells.map(headCell => (
                                <TableCell key={headCell.id} align="left" width={'30%'}>
                                    <Typography variant="body" size="medium">
                                        {headCell.label}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows?.map((row: Data, index: any) => {
                            const isItemSelected = isSelected(row.variable);
                            const labelId = `enhanced-table-checkbox-${index}`;
                            return (
                                <TableRow
                                    hover
                                    role="checkbox"
                                    aria-checked={isItemSelected}
                                    tabIndex={-1}
                                    key={row.variable}
                                    selected={isItemSelected}
                                    sx={{cursor: 'pointer', borderTop: '1px solid rgba(224, 224, 224, 1)'}}>
                                    <TableCell align="left">
                                        <Checkbox
                                            checked={isItemSelected}
                                            onClick={_event => handleClick(row.variable)}
                                            inputProps={{
                                                'aria-labelledby': labelId
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell component="th" id={labelId} scope="row" padding="none" align="left" width={'50%'}>
                                        <Typography variant="body" size="medium" pl={2}>
                                            {row.variable}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        {row?.value?.map((value: string) => (
                                            <Typography variant="body" size="medium" sx={{padding: '13px 0'}}>
                                                {value}
                                            </Typography>
                                        ))}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SelectVariabledDBA;
